import React, {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {CircularProgress} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {enqueueSnackbar} from 'notistack';
import {EstimateTreeDto} from '../../../../shared/dtos/estimate-tree.dto';
import {LatLngDto} from '../../../../shared/dtos/estimate-drag-path.dto';
import {googleMapsLoader} from '../../../../shared/config/google-maps';
import {useLocationPermissions} from '../../../../components/LocationPermissionsContext';
import {
  ArtDefs, COLORS, CraneArt, CraneLine, GATE_TURN_HANDLE_DISTANCE, GATE_TURN_HANDLE_RADIUS, GateArt, GateNumber, GateTurnHandle,
  HANDLE_HALF_ANGLE_DEG, HalfMoon, LeaderArrow, MachineNumber, PILE_PX, ReachPill, RouteHandle, StagingPile, TruckArt, WorkItemPin,
  halfMoonBand, workItemLabel,
} from './map-art';
import {
  CHIPPER_ASPECT, CHIPPER_BED_CENTER_FRACTION_FROM_REAR, CHIPPER_BODY_WIDTH_FRACTION, CHIPPER_LENGTH_METERS, CRANE_ART_ASPECT,
  CRANE_ART_PER_TRUCK, CRANE_LENGTH_METERS, CRANE_NUMBER_FRACTION, CRANE_TURRET_FRACTION, bearingDeg, chipperRearCenter, craneRear,
  craneTurretFromRear, defaultPileSpot, distanceMeters, feetInches, metersPerPx,
} from './map-geometry';
import {SpiderDisplay, spiderLayout, tipFromMarkerCenter} from './map-spiderfy';
import {SITE_ZOOM, SiteMap, localIds} from './useSiteMap';
import {AddMenu, GatePanel, HintCard, MachinePanel, PanelChip, chipLabel, gatePanelActions} from './SiteMapPanels';
import './JobSiteMap.css';

/*
 * The job site map beside the record (SQ-621): everything Android's map does
 * (sections/estimate/map/MapWorkspaceScreen.kt), the same way, with a mouse as
 * well as a finger.
 *
 * Like Android, nothing here is a Maps marker. One overlay in the map's own
 * pane draws every item in screen pixels (so it moves with the map frame for
 * frame) and lays an invisible grab area over each, smallest last so it wins;
 * a press on one is the item's, every other press is the map's. A press that
 * travels past a few pixels drags (the item follows the pointer's travel,
 * never jumping to it); one that doesn't is a tap.
 */

/** Below this zoom the truck and crane stop shrinking: true size at the site zoom and closer. */
const TRUE_SCALE_LOCK_ZOOM = SITE_ZOOM;
/** Android's cover gives up after 8 s: a map that cannot authenticate never says it loaded. */
const MAP_LOAD_TIMEOUT_MS = 8000;
const FIT_PADDING = 128;

const WORK_ITEM_TOUCH_PX = 44;
const GATE_TOUCH_WIDTH = 44;
const GATE_TOUCH_HEIGHT = 38;
const ROUTE_HANDLE_TOUCH_PX = 30;
const MACHINE_TOUCH_SLOP_METERS = 2;
/** Past the half moon's band on every side, so it is easy to grab. */
const CRESCENT_SLOP_PX = 16;

const HANDLE_HALF = HANDLE_HALF_ANGLE_DEG * Math.PI / 180;

type DragTarget =
  | {kind: 'item'; id: number}
  | {kind: 'gate'; id: number}
  | {kind: 'truck'; id: number}
  | {kind: 'crane'; id: number}
  | {kind: 'pile'; id: number}
  | {kind: 'bend'; pathId: number; index: number}
  | {kind: 'mid'; pathId: number; segment: number};

const sameTarget = (a: DragTarget, b: DragTarget) => JSON.stringify(a) === JSON.stringify(b);

interface Drag { target: DragTarget; position: LatLngDto }

/** What a press on one grab area does. */
interface Hit {
  onTap?: () => void;
  /** Moves: the item's own point, which the drag carries. */
  drag?: {target: DragTarget; anchor: LatLngDto};
  /** Turns: about this point, from this heading. */
  turn?: {pivot: LatLngDto; heading: number; onTurning: (heading: number) => void; onTurned: (heading: number) => void; onAbandoned: () => void};
}

type Selected = {kind: 'gate' | 'truck' | 'crane'; id: number} | undefined;

interface Pt { x: number; y: number }

/** A heading's direction on screen (the web map never turns, so north is up). */
const dirOf = (headingDeg: number): Pt => ({x: Math.sin(headingDeg * Math.PI / 180), y: -Math.cos(headingDeg * Math.PI / 180)});
const along = (p: Pt, heading: number, px: number): Pt => {
  const d = dirOf(heading);
  return {x: p.x + d.x * px, y: p.y + d.y * px};
};

/** The half moon's proportions, from the machine it sits in front of (Android crescentGeometry). */
const crescent = (lengthPx: number, bodyWidthPx = lengthPx * CHIPPER_ASPECT * CHIPPER_BODY_WIDTH_FRACTION, nosePx = lengthPx, gapFactor = 0.133) => {
  const radius = Math.max(14, (bodyWidthPx / 2) / Math.sin(HANDLE_HALF));
  const thickness = radius * 0.533;
  return {radius, thickness, centerOffset: nosePx + radius * gapFactor + thickness / 2 - radius};
};

/** True when a pointer is a mouse or trackpad: the hint then says so. */
const finePointer = () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;

export interface JobSiteMapProps {
  siteMap: SiteMap;
  estimateLat: number;
  estimateLng: number;
  trees: EstimateTreeDto[];
  editable: boolean;
  /** A work item was tapped: bring it up in the record. */
  onOpenWorkItem: (treeId: number) => void;
}

const JobSiteMap = ({siteMap, estimateLat, estimateLng, trees, editable, onOpenWorkItem}: JobSiteMapProps) => {
  const canEdit = editable;
  const view = siteMap.view.current;
  const mapDiv = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();
  const overlayRef = useRef<google.maps.OverlayView & {container: HTMLDivElement}>();
  const [container, setContainer] = useState<HTMLDivElement>();
  const [, setFrame] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [selected, setSelectedState] = useState<Selected>();
  const [hintDismissed, setHintDismissed] = useState(view.hintDismissed);
  const [drag, setDrag] = useState<Drag>();
  const [headingOverrides, setHeadingOverrides] = useState<Record<string, number>>({});
  /** truck id → the work item whose route is on (shapeable): one per truck. */
  const [liveRoutes, setLiveRoutes] = useState<Record<number, number>>({});
  const [pileNote, setPileNote] = useState<{text: string; n: number}>();
  const [me, setMe] = useState<LatLngDto>();
  const {hasLocationPermission, requestLocation} = useLocationPermissions();
  const mouse = useMemo(finePointer, []);

  const dismissHint = () => {
    view.hintDismissed = true;
    setHintDismissed(true);
  };
  /** One thing open at a time; nothing closes them all. */
  const select = (next: Selected) => {
    dismissHint();
    setSelectedState(next);
  };

  // ---- The map --------------------------------------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    const listeners: google.maps.MapsEventListener[] = [];
    const loader = googleMapsLoader();
    (async () => {
      const {Map, OverlayView} = await loader.importLibrary('maps') as google.maps.MapsLibrary;
      if (cancelled || !mapDiv.current) return;
      const created = new Map(mapDiv.current, {
        center: view.center ?? {lat: estimateLat, lng: estimateLng},
        zoom: view.zoom ?? SITE_ZOOM,
        mapTypeId: 'satellite',
        tilt: 0,
        disableDefaultUI: true,
        clickableIcons: false,
        // The wheel and a trackpad pinch zoom without a modifier key; Android shows no + / - and neither does this.
        gestureHandling: 'greedy',
        keyboardShortcuts: false,
      });

      class SiteOverlay extends OverlayView {
        container = document.createElement('div');
        onAdd() { this.getPanes()?.overlayMouseTarget.appendChild(this.container); }
        draw() { setFrame(f => f + 1); }
        onRemove() { this.container.remove(); }
      }
      const overlay = new SiteOverlay();
      overlay.container.className = 'sm-overlay';
      overlay.setMap(created);
      overlayRef.current = overlay;
      setContainer(overlay.container);

      listeners.push(
        created.addListener('bounds_changed', () => setFrame(f => f + 1)),
        created.addListener('idle', () => {
          const c = created.getCenter();
          if (c) view.center = {lat: c.lat(), lng: c.lng()};
          view.zoom = created.getZoom() ?? view.zoom;
        }),
        created.addListener('dragstart', () => { view.framed = true; }),
        google.maps.event.addListenerOnce(created, 'tilesloaded', () => setLoaded(true)),
      );
      // Satellite imagery stops at a zoom that depends on the place: closer is a grey "no imagery" screen.
      new google.maps.MaxZoomService().getMaxZoomAtLatLng({lat: estimateLat, lng: estimateLng})
        .then(r => { if (!cancelled && r?.zoom) created.setOptions({maxZoom: r.zoom}); })
        .catch(() => undefined);
      setMap(created);
    })().catch(() => setTimedOut(true));
    return () => {
      cancelled = true;
      listeners.forEach(l => l.remove());
      overlayRef.current?.setMap(null);
      overlayRef.current = undefined;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A wheel or a press on the map is the person moving it: it is never re-framed from then on.
  useEffect(() => {
    const el = mapDiv.current;
    if (!el) return undefined;
    const moved = () => { view.framed = true; };
    el.addEventListener('wheel', moved, {passive: true});
    return () => el.removeEventListener('wheel', moved);
  }, [view]);

  useEffect(() => {
    if (loaded) return undefined;
    const timer = setTimeout(() => setTimedOut(true), MAP_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [loaded]);

  // Tapping the empty map closes whatever is open.
  useEffect(() => {
    if (!map) return undefined;
    const l = map.addListener('click', () => {
      // The click a tap on an item that only opens (view mode) also gives the map: that tap already chose.
      if (Date.now() < itemTappedUntil.current) return;
      dismissHint();
      setSelectedState(undefined);
    });
    return () => l.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Opens framed on everything, once the imagery is up, unless the person already moved it.
  useEffect(() => {
    if (!map || !loaded || view.framed) return;
    view.framed = true;
    const points = [
      ...trees.map(t => ({lat: t.lat, lng: t.lng})),
      ...siteMap.gates.map(g => ({lat: g.lat, lng: g.lng})),
      ...siteMap.chippers.map(c => ({lat: c.lat, lng: c.lng})),
      ...siteMap.cranes.map(c => ({lat: c.lat, lng: c.lng})),
    ];
    if (points.length >= 2) {
      const bounds = new google.maps.LatLngBounds();
      points.forEach(p => bounds.extend(p));
      map.fitBounds(bounds, FIT_PADDING);
    } else if (points.length === 1) {
      map.setCenter(points[0]);
      map.setZoom(SITE_ZOOM);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, loaded]);

  // Your own location, as the blue dot, while the browser allows it.
  useEffect(() => {
    if (!hasLocationPermission || !navigator.geolocation) {
      setMe(undefined);
      return undefined;
    }
    const watch = navigator.geolocation.watchPosition(p => setMe({lat: p.coords.latitude, lng: p.coords.longitude}), () => undefined,
      {enableHighAccuracy: true, maximumAge: 10000});
    return () => navigator.geolocation.clearWatch(watch);
  }, [hasLocationPermission]);

  const askLocation = async () => {
    const at = await requestLocation();
    if (at) setMe(at);
    else enqueueSnackbar('Location is off. You can still use the map. Enable location in your browser settings to center on you.', {variant: 'info', autoHideDuration: 6000});
  };

  // A tapped staging pile says whose it is for two seconds.
  useEffect(() => {
    if (!pileNote) return undefined;
    const timer = setTimeout(() => setPileNote(undefined), 2000);
    return () => clearTimeout(timer);
  }, [pileNote]);

  // ---- Layout ----------------------------------------------------------------------------------------------------

  const zoom = map?.getZoom() ?? SITE_ZOOM;
  const layoutZoom = Math.round(zoom * 10) / 10;
  useEffect(() => { siteMap.setViewZoom(layoutZoom); }, [siteMap, layoutZoom]);

  const treeNumbers = useMemo(() => localIds(trees.map(t => t.id)), [trees]);
  const gateNumbers = useMemo(() => localIds(siteMap.gates.map(g => g.id)), [siteMap.gates]);
  const truckNumbers = useMemo(() => localIds(siteMap.chippers.map(c => c.id)), [siteMap.chippers]);
  const craneNumbers = useMemo(() => localIds(siteMap.cranes.map(c => c.id)), [siteMap.cranes]);

  // The pin just dropped keeps its place through the next layout; its neighbours make room.
  const frozen = useRef<number | undefined>(undefined);
  const spiderKey = trees.map(t => `${t.id}:${t.lat}:${t.lng}`).join('|');
  const displays: SpiderDisplay[] = useMemo(
    () => spiderLayout(trees.map(t => ({id: t.id, lat: t.lat, lng: t.lng})), layoutZoom, frozen.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [spiderKey, layoutZoom]);
  useEffect(() => { frozen.current = undefined; }, [spiderKey, layoutZoom]);

  const draggedTo = (target: DragTarget) => drag && sameTarget(drag.target, target) ? drag.position : undefined;

  // Where each work item's arrow points; one being dragged carries its arrow with it.
  const tips = new Map<number, LatLngDto>(displays.map(d => {
    const held = draggedTo({kind: 'item', id: d.id});
    return [d.id, held ? {lat: d.tipLat + held.lat - d.lat, lng: d.tipLng + held.lng - d.lng} : {lat: d.tipLat, lng: d.tipLng}];
  }));

  const liveTurret = (c: {id: number; lat: number; lng: number; heading?: number | null}): LatLngDto => {
    const held = draggedTo({kind: 'crane', id: c.id});
    if (held) return held;
    const override = headingOverrides[`crane:${c.id}`];
    const stored = {lat: c.lat, lng: c.lng};
    return override === undefined ? stored : craneTurretFromRear(craneRear(stored, c.heading ?? 0), override);
  };

  const reaches = siteMap.craneConnections.flatMap(link => {
    const crane = siteMap.cranes.find(c => c.id === link.craneId);
    const tip = tips.get(link.treeId);
    if (!crane || !tip) return [];
    const turret = liveTurret(crane);
    const rest = link.pileLat != null && link.pileLng != null ? {lat: link.pileLat, lng: link.pileLng} : defaultPileSpot(turret, tip);
    return [{link, turret, tip, rest, pile: draggedTo({kind: 'pile', id: link.id}) ?? rest}];
  });

  const truckRear = (c: {id: number; lat: number; lng: number; heading?: number | null}) =>
    draggedTo({kind: 'truck', id: c.id}) ?? chipperRearCenter({lat: c.lat, lng: c.lng}, c.heading ?? 0);

  // ---- Panels' chips ---------------------------------------------------------------------------------------------

  const sortedTrees = [...trees].sort((a, b) => (treeNumbers.get(a.id) ?? 0) - (treeNumbers.get(b.id) ?? 0));
  const truckChips = (truckId: number): PanelChip[] => sortedTrees.flatMap<PanelChip>(t => {
    const path = siteMap.dragPaths.find(p => p.treeId === t.id);
    const label = chipLabel(t, treeNumbers.get(t.id));
    if (!path) return [{treeId: t.id, label, checked: false, live: false}];
    // On another truck: out of this panel's pool until it is unchecked there.
    return path.chipperTruckId === truckId ? [{treeId: t.id, label, checked: true, live: liveRoutes[truckId] === t.id}] : [];
  });
  // A work item on a truck is still offered: a crane and a truck do different jobs on one tree.
  const craneChips = (craneId: number): PanelChip[] => sortedTrees.flatMap<PanelChip>(t => {
    const link = siteMap.craneConnections.find(c => c.treeId === t.id);
    const label = chipLabel(t, treeNumbers.get(t.id));
    if (!link) return [{treeId: t.id, label, checked: false, live: false}];
    return link.craneId === craneId ? [{treeId: t.id, label, checked: true, live: false}] : [];
  });

  const panelTruck = selected?.kind === 'truck' ? siteMap.chippers.find(c => c.id === selected.id) : undefined;
  const panelCrane = selected?.kind === 'crane' ? siteMap.cranes.find(c => c.id === selected.id) : undefined;
  const panelGate = selected?.kind === 'gate' ? siteMap.gates.find(g => g.id === selected.id) : undefined;

  // ---- Gestures ------------------------------------------------------------------------------------------------

  const hits = useRef(new Map<string, Hit>());
  const itemTappedUntil = useRef(0);
  const nextHits = new Map<string, Hit>();
  const latest = useRef({onDropped: (_: Drag) => undefined as void});

  const onDropped = (dropped: Drag) => {
    setDrag(undefined);
    const t = dropped.target;
    const at = dropped.position;
    switch (t.kind) {
      case 'item': {
        // The arrow's tip is the saved spot: the dropped circle stands for the tip south of it, so the pin doesn't jump.
        const tip = tipFromMarkerCenter(at, map?.getZoom() ?? layoutZoom);
        frozen.current = t.id;
        void siteMap.moveWorkItem(t.id, tip.lat, tip.lng);
        break;
      }
      case 'gate': void siteMap.moveGate(t.id, at); break;
      case 'truck': siteMap.moveChipperTruck(t.id, at); break;
      case 'crane': void siteMap.moveCrane(t.id, at); break;
      case 'pile': void siteMap.movePile(t.id, at); break;
      case 'bend': {
        const points = siteMap.dragPaths.find(p => p.id === t.pathId)?.points;
        if (points && t.index >= 1 && t.index <= points.length - 2) {
          const next = [...points];
          next[t.index] = at;
          void siteMap.movePathPoint(t.pathId, next, t.index);
        }
        break;
      }
      case 'mid': {
        const points = siteMap.dragPaths.find(p => p.id === t.pathId)?.points;
        if (points && t.segment >= 0 && t.segment <= points.length - 2) {
          // Dropping a middle point makes a new bend where it landed.
          const next = [...points];
          next.splice(t.segment + 1, 0, at);
          void siteMap.movePathPoint(t.pathId, next, t.segment + 1);
        }
        break;
      }
    }
  };
  latest.current.onDropped = onDropped;

  useEffect(() => {
    const el = container;
    if (!el || !map) return undefined;
    const overlay = overlayRef.current;
    const hitOf = (target: EventTarget | null) => (target as Element | null)?.closest?.('[data-hit]') as HTMLElement | null;
    // The map never sees a press that lands on something that moves: no pan, no click that would close the panel, no
    // double-click zoom. One that can only be opened (view mode) lets the press through, so the map still pans from it, as
    // Android's read-only map does.
    const movable = (node: HTMLElement | null) => !!node && node.dataset.tapOnly === undefined;
    const shield = (e: Event) => { if (movable(hitOf(e.target))) e.stopPropagation(); };
    const shielded = ['mousedown', 'touchstart', 'click', 'dblclick', 'contextmenu'];
    shielded.forEach(type => el.addEventListener(type, shield));

    const onDown = (e: PointerEvent) => {
      const node = hitOf(e.target);
      const hit = node && hits.current.get(node.dataset.hit ?? '');
      if (!node || !hit) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (movable(node)) {
        e.stopPropagation();
        e.preventDefault();
      }
      const projection = () => overlay?.getProjection();
      const mapRect = () => mapDiv.current!.getBoundingClientRect();
      const slop = e.pointerType === 'mouse' ? 4 : 8;
      const downX = e.clientX;
      const downY = e.clientY;
      let moved = false;
      let lastAngle = 0;
      let liveHeading = hit.turn?.heading ?? 0;
      let lastDrag: Drag | undefined;
      const angleAt = (x: number, y: number) => {
        const p = projection()?.fromLatLngToContainerPixel(new google.maps.LatLng(hit.turn!.pivot.lat, hit.turn!.pivot.lng));
        const r = mapRect();
        return p ? Math.atan2(y - (r.top + p.y), x - (r.left + p.x)) * 180 / Math.PI : 0;
      };
      const move = (ev: PointerEvent) => {
        if (ev.pointerId !== e.pointerId) return;
        const dx = ev.clientX - downX;
        const dy = ev.clientY - downY;
        if (!moved) {
          if (Math.hypot(dx, dy) < slop) return;
          if (!hit.drag && !hit.turn) {
            // A press on something that only opens became a pan of the map: no tap.
            end(ev, true);
            return;
          }
          moved = true;
          dismissHint();
          document.body.classList.add(hit.turn ? 'sm-turning' : 'sm-grabbing');
          if (hit.turn) lastAngle = angleAt(downX, downY);
        }
        ev.preventDefault();
        if (hit.turn) {
          const angle = angleAt(ev.clientX, ev.clientY);
          let step = angle - lastAngle;
          if (step > 180) step -= 360;
          if (step < -180) step += 360;
          lastAngle = angle;
          liveHeading += step;
          hit.turn.onTurning(liveHeading);
        } else if (hit.drag) {
          // The item moves by the pointer's travel from where it went down, against its live place on screen.
          const p = projection();
          const from = p?.fromLatLngToContainerPixel(new google.maps.LatLng(hit.drag.anchor.lat, hit.drag.anchor.lng));
          const to = from && p?.fromContainerPixelToLatLng(new google.maps.Point(from.x + dx, from.y + dy));
          if (!to) return;
          lastDrag = {target: hit.drag.target, position: {lat: to.lat(), lng: to.lng()}};
          setDrag(lastDrag);
        }
      };
      const end = (ev: PointerEvent, cancelled: boolean) => {
        if (ev.pointerId !== e.pointerId) return;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel);
        document.body.classList.remove('sm-grabbing', 'sm-turning');
        if (!moved) {
          if (!cancelled && hit.onTap) {
            itemTappedUntil.current = Date.now() + 500;
            hit.onTap();
          }
          return;
        }
        if (hit.turn) {
          if (cancelled) hit.turn.onAbandoned();
          else hit.turn.onTurned(((liveHeading % 360) + 360) % 360);
        } else if (lastDrag && !cancelled) {
          latest.current.onDropped(lastDrag);
        } else {
          setDrag(undefined);
        }
      };
      const up = (ev: PointerEvent) => end(ev, false);
      const cancel = (ev: PointerEvent) => end(ev, true);
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel);
    };
    el.addEventListener('pointerdown', onDown);
    return () => {
      shielded.forEach(type => el.removeEventListener(type, shield));
      el.removeEventListener('pointerdown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [container, map]);

  // ---- Drawing ---------------------------------------------------------------------------------------------------

  const projection = overlayRef.current?.getProjection();
  const px = (p: LatLngDto): Pt => {
    const at = projection!.fromLatLngToDivPixel(new google.maps.LatLng(p.lat, p.lng))!;
    return {x: at.x, y: at.y};
  };

  let svg: React.ReactNode = null;
  if (map && projection && container && mapDiv.current) {
    const width = mapDiv.current.clientWidth;
    const height = mapDiv.current.clientHeight;
    const corner = projection.fromContainerPixelToLatLng(new google.maps.Point(0, 0));
    const origin = corner ? px({lat: corner.lat(), lng: corner.lng()}) : {x: 0, y: 0};
    // A screen past every edge, so a pan never shows the edge before the next frame.
    const box = {x: origin.x - width, y: origin.y - height, w: width * 3, h: height * 3};

    const lockedMpp = (lat: number) => metersPerPx(lat, Math.max(layoutZoom, TRUE_SCALE_LOCK_ZOOM));
    const hitRect = (key: string, hit: Hit, c: Pt, w: number, h: number, rotation: number, className: string, label?: string) => {
      nextHits.set(key, hit);
      return (
        <rect key={key} data-hit={key} data-tap-only={hit.drag || hit.turn ? undefined : ''} className={`sm-hit ${className}`}
              x={-w / 2} y={-h / 2} width={w} height={h}
              transform={`translate(${c.x} ${c.y}) rotate(${rotation})`} aria-label={label}/>
      );
    };
    // Things that move show the grab hand; in view mode they only open.
    const grab = canEdit ? 'sm-hit--move' : 'sm-hit--tap';

    // Drag routes: under everything. A work item on a crane starts its route at its pile.
    const pileOf = new Map(reaches.map(r => [r.link.treeId, r.pile]));
    const routes = siteMap.dragPaths.map(path => {
      let points = path.points;
      if (points.length < 2) return null;
      const pile = pileOf.get(path.treeId);
      const truck = siteMap.chippers.find(c => c.id === path.chipperTruckId);
      if (pile) points = [pile, ...points.slice(1)];
      else if (draggedTo({kind: 'item', id: path.treeId})) points = [tips.get(path.treeId)!, ...points.slice(1)];
      if (truck && draggedTo({kind: 'truck', id: truck.id})) points = [...points.slice(0, -1), truckRear(truck)];
      if (drag?.target.kind === 'bend' && drag.target.pathId === path.id) {
        points = [...points];
        points[drag.target.index] = drag.position;
      } else if (drag?.target.kind === 'mid' && drag.target.pathId === path.id) {
        points = [...points];
        points.splice(drag.target.segment + 1, 0, drag.position);
      }
      const live = canEdit && liveRoutes[path.chipperTruckId] === path.treeId;
      return {path, points, live, stored: path.points};
    }).filter((r): r is NonNullable<typeof r> => !!r);

    const trucks = siteMap.chippers.map(c => {
      const heading = headingOverrides[`truck:${c.id}`] ?? c.heading ?? 0;
      const rear = px(truckRear(c));
      const lengthPx = CHIPPER_LENGTH_METERS / lockedMpp(c.lat);
      return {c, heading, rear, lengthPx, moon: crescent(lengthPx)};
    });
    const craneDrawings = siteMap.cranes.map(c => {
      const heading = headingOverrides[`crane:${c.id}`] ?? c.heading ?? 0;
      const artPx = CRANE_LENGTH_METERS * CRANE_ART_PER_TRUCK / lockedMpp(c.lat);
      const turretLatLng = liveTurret(c);
      const turret = px(turretLatLng);
      const moon = crescent(artPx, artPx * CRANE_ART_ASPECT * 0.8, artPx * (1 - CRANE_TURRET_FRACTION), 0.03);
      return {c, heading, artPx, turret, turretLatLng, moon};
    });

    // Grab areas, in Android's order: the machines, gates, work items, piles, then the route points; the turn handles last.
    const hitLayer: React.ReactNode[] = [];
    trucks.forEach(({c, heading, rear, lengthPx}) => {
      const slop = MACHINE_TOUCH_SLOP_METERS / lockedMpp(c.lat);
      const center = along(rear, heading, lengthPx / 2);
      hitLayer.push(hitRect(`truck:${c.id}`, {
        onTap: () => select({kind: 'truck', id: c.id}),
        drag: canEdit ? {target: {kind: 'truck', id: c.id}, anchor: chipperRearCenter({lat: c.lat, lng: c.lng}, c.heading ?? 0)} : undefined,
      }, center, lengthPx * CHIPPER_ASPECT + 2 * slop, lengthPx + 2 * slop, heading, grab, `Truck & Chipper ${truckNumbers.get(c.id)}`));
    });
    craneDrawings.forEach(({c, heading, artPx, turret}) => {
      const slop = MACHINE_TOUCH_SLOP_METERS / lockedMpp(c.lat);
      const center = along(turret, heading, (0.5 - CRANE_TURRET_FRACTION) * artPx);
      hitLayer.push(hitRect(`crane:${c.id}`, {
        onTap: () => select({kind: 'crane', id: c.id}),
        drag: canEdit ? {target: {kind: 'crane', id: c.id}, anchor: {lat: c.lat, lng: c.lng}} : undefined,
      }, center, artPx * CRANE_ART_ASPECT + 2 * slop, artPx + 2 * slop, heading, grab, `Crane ${craneNumbers.get(c.id)}`));
    });
    siteMap.gates.forEach(g => {
      const at = px({lat: g.lat, lng: g.lng});
      hitLayer.push(hitRect(`gate:${g.id}`, {
        onTap: () => select({kind: 'gate', id: g.id}),
        drag: canEdit ? {target: {kind: 'gate', id: g.id}, anchor: {lat: g.lat, lng: g.lng}} : undefined,
      }, at, GATE_TOUCH_WIDTH, GATE_TOUCH_HEIGHT, 0, grab, `Gate ${gateNumbers.get(g.id)}`));
    });
    displays.forEach(d => {
      const t = trees.find(x => x.id === d.id);
      hitLayer.push(hitRect(`item:${d.id}`, {
        onTap: () => { dismissHint(); onOpenWorkItem(d.id); },
        drag: canEdit ? {target: {kind: 'item', id: d.id}, anchor: {lat: d.lat, lng: d.lng}} : undefined,
      }, px({lat: d.lat, lng: d.lng}), WORK_ITEM_TOUCH_PX, WORK_ITEM_TOUCH_PX, 0, grab,
      t ? `${treeNumbers.get(t.id)} ${workItemLabel(t.workItemType)}` : undefined));
    });
    const showPileNote = (connectionId: number) => {
      dismissHint();
      const link = siteMap.craneConnections.find(c => c.id === connectionId);
      const t = link && trees.find(x => x.id === link.treeId);
      if (t) setPileNote(n => ({text: `Staging pile for #${treeNumbers.get(t.id) ?? ''} ${workItemLabel(t.workItemType)}`, n: (n?.n ?? 0) + 1}));
    };
    // Where each pile rests, not where a drag has it: a grab area that followed the pointer would move the frame its travel is measured in.
    reaches.forEach(r => hitLayer.push(hitRect(`pile:${r.link.id}`, {
      onTap: () => showPileNote(r.link.id),
      drag: canEdit ? {target: {kind: 'pile', id: r.link.id}, anchor: r.rest} : undefined,
    }, px(r.rest), PILE_PX, PILE_PX, 0, grab, 'Staging pile')));
    routes.filter(r => r.live).forEach(({path, stored}) => {
      for (let i = 1; i <= stored.length - 2; i++) {
        hitLayer.push(hitRect(`bend:${path.id}:${i}`, {drag: {target: {kind: 'bend', pathId: path.id, index: i}, anchor: stored[i]}},
          px(stored[i]), ROUTE_HANDLE_TOUCH_PX, ROUTE_HANDLE_TOUCH_PX, 0, 'sm-hit--move', 'Route bend'));
      }
      for (let s = 0; s <= stored.length - 2; s++) {
        const mid = {lat: (stored[s].lat + stored[s + 1].lat) / 2, lng: (stored[s].lng + stored[s + 1].lng) / 2};
        hitLayer.push(hitRect(`mid:${path.id}:${s}`, {drag: {target: {kind: 'mid', pathId: path.id, segment: s}, anchor: mid}},
          px(mid), ROUTE_HANDLE_TOUCH_PX, ROUTE_HANDLE_TOUCH_PX, 0, 'sm-hit--move', 'Route midpoint'));
      }
    });
    const setOverride = (key: string, heading?: number) => setHeadingOverrides(o => {
      const next = {...o};
      if (heading === undefined) delete next[key]; else next[key] = heading;
      return next;
    });
    if (canEdit) {
      siteMap.gates.forEach(g => {
        const key = `gate:${g.id}`;
        const heading = headingOverrides[key] ?? g.heading ?? 0;
        const center = draggedTo({kind: 'gate', id: g.id}) ?? {lat: g.lat, lng: g.lng};
        const handle = along(px(center), heading, GATE_TURN_HANDLE_DISTANCE);
        const size = GATE_TURN_HANDLE_RADIUS * 2 + 14;
        hitLayer.push(hitRect(`turn-gate:${g.id}`, {
          onTap: () => select({kind: 'gate', id: g.id}),
          turn: {pivot: center, heading, onTurning: h => setOverride(key, h), onAbandoned: () => setOverride(key),
            onTurned: h => { void siteMap.rotateGate(g.id, h); setOverride(key); }},
        }, handle, size, size, heading, 'sm-hit--turn', `Turn gate ${gateNumbers.get(g.id)}`));
      });
      const moonHit = (key: string, hit: Hit, arcCenter: Pt, radius: number, thickness: number, heading: number, label: string) => {
        nextHits.set(key, hit);
        hitLayer.push(
          <path key={key} data-hit={key} className="sm-hit sm-hit--turn sm-hit--band" d={halfMoonBand(radius)} fill="none"
                strokeWidth={thickness + 2 * CRESCENT_SLOP_PX} strokeLinecap="round"
                transform={`translate(${arcCenter.x} ${arcCenter.y}) rotate(${heading})`} aria-label={label}/>,
        );
      };
      trucks.forEach(({c, heading, rear, moon}) => {
        const key = `truck:${c.id}`;
        moonHit(`turn-truck:${c.id}`, {
          onTap: () => select({kind: 'truck', id: c.id}),
          turn: {pivot: truckRear(c), heading, onTurning: h => setOverride(key, h), onAbandoned: () => setOverride(key),
            onTurned: h => { siteMap.rotateChipperTruck(c.id, h); setOverride(key); }},
        }, along(rear, heading, moon.centerOffset), moon.radius, moon.thickness, heading, `Turn Truck & Chipper ${truckNumbers.get(c.id)}`);
      });
      craneDrawings.forEach(({c, heading, turret, moon}) => {
        const key = `crane:${c.id}`;
        moonHit(`turn-crane:${c.id}`, {
          onTap: () => select({kind: 'crane', id: c.id}),
          // A crane turns about the rear of its truck, like the truck & chipper; the half moon sits off the end of the boom.
          turn: {pivot: craneRear({lat: c.lat, lng: c.lng}, c.heading ?? 0), heading, onTurning: h => setOverride(key, h), onAbandoned: () => setOverride(key),
            onTurned: h => { void siteMap.rotateCrane(c.id, h); setOverride(key); }},
        }, along(turret, heading, moon.centerOffset), moon.radius, moon.thickness, heading, `Turn crane ${craneNumbers.get(c.id)}`);
      });
    }
    hits.current = nextHits;

    const path = (points: LatLngDto[]) => points.map((p, i) => {
      const at = px(p);
      return `${i ? 'L' : 'M'}${at.x} ${at.y}`;
    }).join(' ');

    svg = (
      <svg className="sm-svg" style={{left: box.x, top: box.y}} width={box.w} height={box.h} viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`}>
        <ArtDefs/>
        <g className="sm-art">
          {routes.map(r => <path key={`route-${r.path.id}`} d={path(r.points)} fill="none" stroke={COLORS.dragPath} strokeWidth={2}/>)}
          {craneDrawings.map(({c, heading, artPx, turret}) => <CraneArt key={`crane-${c.id}`} x={turret.x} y={turret.y} artPx={artPx} heading={heading}/>)}
          {siteMap.gates.map(g => {
            const at = px(draggedTo({kind: 'gate', id: g.id}) ?? {lat: g.lat, lng: g.lng});
            return <GateArt key={`gate-${g.id}`} x={at.x} y={at.y} heading={headingOverrides[`gate:${g.id}`] ?? g.heading ?? 0}/>;
          })}
          {trucks.map(({c, heading, rear, lengthPx}) => <TruckArt key={`truck-${c.id}`} x={rear.x} y={rear.y} lengthPx={lengthPx} heading={heading}/>)}
          {reaches.map(r => {
            const a = px(r.turret);
            const b = px(r.tip);
            const p = px(r.pile);
            const bearing = bearingDeg(r.turret, r.tip);
            const rotation = bearing - 90 + (bearing > 180 ? 180 : 0);
            return (
              <g key={`reach-${r.link.id}`}>
                <CraneLine x1={a.x} y1={a.y} x2={b.x} y2={b.y}/>
                <CraneLine x1={b.x} y1={b.y} x2={p.x} y2={p.y} dashed/>
                <ReachPill x={(a.x + b.x) / 2} y={(a.y + b.y) / 2} rotation={rotation} text={feetInches(distanceMeters(r.turret, r.tip))}/>
              </g>
            );
          })}
          {displays.map(d => {
            const held = draggedTo({kind: 'item', id: d.id});
            const shift = held ? {lat: held.lat - d.lat, lng: held.lng - d.lng} : {lat: 0, lng: 0};
            const base = px({lat: d.baseLat + shift.lat, lng: d.baseLng + shift.lng});
            const tip = px({lat: d.tipLat + shift.lat, lng: d.tipLng + shift.lng});
            return <LeaderArrow key={`leader-${d.id}`} bx={base.x} by={base.y} tx={tip.x} ty={tip.y}/>;
          })}
          {reaches.map(r => {
            const p = px(r.pile);
            return <StagingPile key={`pile-${r.link.id}`} x={p.x} y={p.y}/>;
          })}
          {displays.map(d => {
            const t = trees.find(x => x.id === d.id);
            if (!t) return null;
            const at = px(draggedTo({kind: 'item', id: d.id}) ?? {lat: d.lat, lng: d.lng});
            return <WorkItemPin key={`pin-${d.id}`} id={d.id} x={at.x} y={at.y} type={t.workItemType} label={String(treeNumbers.get(t.id) ?? '')}/>;
          })}
          {siteMap.gates.map(g => {
            const at = px(draggedTo({kind: 'gate', id: g.id}) ?? {lat: g.lat, lng: g.lng});
            return <GateNumber key={`gate-num-${g.id}`} x={at.x} y={at.y} label={String(gateNumbers.get(g.id) ?? '')}/>;
          })}
          {trucks.map(({c, heading, rear, lengthPx}) => {
            const bed = along(rear, heading, lengthPx * CHIPPER_BED_CENTER_FRACTION_FROM_REAR);
            return <MachineNumber key={`truck-num-${c.id}`} x={bed.x} y={bed.y} size={Math.max(11, lengthPx * 0.16)} label={String(truckNumbers.get(c.id) ?? '')}/>;
          })}
          {craneDrawings.map(({c, heading, artPx, turret}) => {
            const bed = along(turret, heading, (CRANE_NUMBER_FRACTION - CRANE_TURRET_FRACTION) * artPx);
            return <MachineNumber key={`crane-num-${c.id}`} x={bed.x} y={bed.y} size={Math.max(11, artPx * 0.14)} label={String(craneNumbers.get(c.id) ?? '')}/>;
          })}
          {canEdit && <>
            {siteMap.gates.map(g => {
              const at = px(draggedTo({kind: 'gate', id: g.id}) ?? {lat: g.lat, lng: g.lng});
              return <GateTurnHandle key={`gate-turn-${g.id}`} x={at.x} y={at.y} heading={headingOverrides[`gate:${g.id}`] ?? g.heading ?? 0}/>;
            })}
            {trucks.map(({c, heading, rear, moon}) => {
              const at = along(rear, heading, moon.centerOffset);
              return <HalfMoon key={`truck-moon-${c.id}`} x={at.x} y={at.y} radius={moon.radius} thickness={moon.thickness} heading={heading}/>;
            })}
            {craneDrawings.map(({c, heading, turret, moon}) => {
              const at = along(turret, heading, moon.centerOffset);
              return <HalfMoon key={`crane-moon-${c.id}`} x={at.x} y={at.y} radius={moon.radius} thickness={moon.thickness} heading={heading}/>;
            })}
            {routes.filter(r => r.live).flatMap(({path: p, stored}) => {
              const held = drag && (drag.target.kind === 'bend' || drag.target.kind === 'mid') && drag.target.pathId === p.id ? drag : undefined;
              const dots: React.ReactNode[] = [];
              for (let i = 1; i <= stored.length - 2; i++) {
                const at = px(held?.target.kind === 'bend' && held.target.index === i ? held.position : stored[i]);
                dots.push(<RouteHandle key={`bend-${p.id}-${i}`} x={at.x} y={at.y}/>);
              }
              for (let s = 0; s <= stored.length - 2; s++) {
                const at = px(held?.target.kind === 'mid' && held.target.segment === s
                  ? held.position : {lat: (stored[s].lat + stored[s + 1].lat) / 2, lng: (stored[s].lng + stored[s + 1].lng) / 2});
                dots.push(<RouteHandle key={`mid-${p.id}-${s}`} x={at.x} y={at.y}/>);
              }
              return dots;
            })}
          </>}
          {me && (() => {
            const at = px(me);
            return (
              <g className="sm-me">
                <circle cx={at.x} cy={at.y} r={14} fill="rgba(66,133,244,0.2)"/>
                <circle cx={at.x} cy={at.y} r={7} fill="#4285F4" stroke="#fff" strokeWidth={2}/>
              </g>
            );
          })()}
        </g>
        <g className="sm-hits">{hitLayer}</g>
      </svg>
    );
  }

  // ---- The page ----------------------------------------------------------------------------------------------

  const empty = trees.length === 0 ? 'No work items on the map yet — add them from the estimate.' : undefined;
  return (
    <div className="sm-root">
      <div ref={mapDiv} className="sm-map"/>
      {container && svg && createPortal(svg, container)}

      {!loaded && !timedOut && (
        <div className="sm-cover">
          <CircularProgress/>
          <div className="sm-cover-text">Loading satellite imagery…</div>
        </div>
      )}
      {!loaded && timedOut && (
        <div className="sm-load-error" role="alert">
          <div className="sm-load-error-title">Map imagery didn't load</div>
          <div>Check the connection. If this site has no Maps API key — or one not registered for it — the map stays blank while everything else keeps working.</div>
        </div>
      )}
      {siteMap.working && <div className="sm-working"><CircularProgress/></div>}

      {!hasLocationPermission && (
        <button type="button" className="sm-locate" onClick={() => void askLocation()} aria-label="Show my location" title="Show my location">
          <LocationOnIcon/>
        </button>
      )}

      <div className="sm-top">
        {panelTruck && (
          <MachinePanel
            number={truckNumbers.get(panelTruck.id) ?? 0}
            title={`Truck & Chipper ${truckNumbers.get(panelTruck.id) ?? 0}`}
            chips={truckChips(panelTruck.id)}
            emptyMessage={empty ?? 'Every work item is already dragging to another truck.'}
            canEdit={canEdit}
            badgeColor={COLORS.chipper}
            badgeText="#fff"
            deleteLabel="Delete truck & chipper"
            showLive
            onToggleConnect={chip => {
              if (chip.checked) {
                void siteMap.disconnectWorkItem(chip.treeId);
                setLiveRoutes(r => {
                  if (r[panelTruck.id] !== chip.treeId) return r;
                  const next = {...r};
                  delete next[panelTruck.id];
                  return next;
                });
              } else {
                void siteMap.connectWorkItem(panelTruck.id, chip.treeId);
                // A fresh route comes on live; whichever route was live for this truck settles.
                setLiveRoutes(r => ({...r, [panelTruck.id]: chip.treeId}));
              }
            }}
            onToggleLive={chip => setLiveRoutes(r => {
              const next = {...r};
              if (chip.live) delete next[panelTruck.id]; else next[panelTruck.id] = chip.treeId;
              return next;
            })}
            onDelete={() => {
              void siteMap.deleteChipperTruck(panelTruck.id);
              setLiveRoutes(r => {
                const next = {...r};
                delete next[panelTruck.id];
                return next;
              });
              setSelectedState(undefined);
            }}
            onClose={() => setSelectedState(undefined)}
          />
        )}
        {panelCrane && (
          <MachinePanel
            number={craneNumbers.get(panelCrane.id) ?? 0}
            title={`Crane ${craneNumbers.get(panelCrane.id) ?? 0}`}
            chips={craneChips(panelCrane.id)}
            emptyMessage={empty ?? 'Every work item is already on another crane.'}
            canEdit={canEdit}
            badgeColor={COLORS.crane}
            badgeText={COLORS.craneOn}
            deleteLabel="Delete crane"
            showLive={false}
            onToggleConnect={chip => void (chip.checked ? siteMap.disconnectCrane(chip.treeId) : siteMap.connectCrane(panelCrane.id, chip.treeId))}
            onToggleLive={() => undefined}
            onDelete={() => {
              void siteMap.deleteCrane(panelCrane.id);
              setSelectedState(undefined);
            }}
            onClose={() => setSelectedState(undefined)}
          />
        )}
        {!canEdit && <div className="sm-view-only">View only — tap Edit on the estimate to make changes</div>}
        {panelGate && (
          <GatePanel
            gate={panelGate}
            number={gateNumbers.get(panelGate.id) ?? 0}
            uploads={siteMap.gateUploads[panelGate.id] ?? []}
            trees={trees}
            treeNumbers={treeNumbers}
            canEdit={canEdit}
            actions={gatePanelActions(siteMap, panelGate.id, () => setSelectedState(undefined))}
          />
        )}
        {loaded && !hintDismissed && !panelTruck && !panelCrane && !panelGate && (
          <HintCard canEdit={canEdit} mouse={mouse} onDismiss={dismissHint}/>
        )}
      </div>

      {pileNote && <div key={pileNote.n} className="sm-note" role="status">{pileNote.text}</div>}

      {canEdit && (
        <AddMenu
          onAddTruck={() => void siteMap.addChipperTruck(created => select({kind: 'truck', id: created.id}))}
          onAddCrane={() => void siteMap.addCrane(created => select({kind: 'crane', id: created.id}))}
          onAddGate={() => void siteMap.addGate(created => select({kind: 'gate', id: created.id}))}
        />
      )}
    </div>
  );
};

export default JobSiteMap;
